import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Cog, GitMerge, Server, ClipboardList, TrendingUp,
  ArrowRight, CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const services = [
  { text: 'Business process automation', icon: Cog },
  { text: 'System integrations', icon: GitMerge },
  { text: 'IT infrastructure consulting', icon: Server },
  { text: 'Project & change management guidance', icon: ClipboardList },
  { text: 'Digital transformation strategy', icon: TrendingUp },
];

const ServicesSystemsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const systemsServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Fearless IT Systems Services",
    "serviceType": "Automation and Technology Consulting",
    "description": "Workflow automation, system integrations, IT infrastructure consulting, project management guidance, and digital transformation strategy for Las Vegas businesses.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Fearless IT",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Las Vegas",
        "addressRegion": "NV",
        "addressCountry": "US"
      },
      "telephone": "(702) 727-1773",
      "url": "https://fearlessit.io"
    },
    "areaServed": {
      "@type": "City",
      "name": "Las Vegas"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Systems & Automation Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Workflow Automation" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "System Integrations" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "IT Infrastructure Consulting" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Project & Change Management" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Digital Transformation Strategy" }}
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="services-systems-page">
      <SEO 
        title="Systems Services - Automation & Technology Consulting"
        description="Fearless IT Systems: Strategic consulting and automation solutions for Las Vegas organizations. Workflow automation, system integrations, IT infrastructure consulting, project management, and digital transformation strategy."
        keywords="business automation Las Vegas, system integrations Nevada, IT consulting Las Vegas, digital transformation Las Vegas, process automation, technology consulting Nevada, workflow automation, enterprise IT"
        url="/services/systems"
        image="https://static.prod-images.emergentagent.com/jobs/1c71d773-84d6-4251-9bae-60816a9265db/images/cde8360c078b050165a213f1e1435b0fb23af5b4eb1491c7df590648f03f2f3a.png"
        serviceName="Automation and Technology Consulting"
        structuredData={systemsServiceSchema}
        isLocalPage={true}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
            <Cog className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Systems Services</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="systems-title">
            Fearless IT Systems
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #D97706, #F59E0B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Automation & Technology Consulting
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            For organizations that need more advanced systems, Fearless IT provides strategic consulting and automation solutions.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-sm font-semibold text-slate-gray uppercase tracking-wider mb-8 text-center">
            Services Include
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {services.map((service, idx) => {
              const IconComponent = service.icon;
              return (
                <div 
                  key={idx} 
                  className="flex items-center gap-4 p-5 bg-gray-50 rounded-xl border border-gray-100 hover:border-amber-200 hover:bg-amber-50/30 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-lg font-medium text-carbon-black">{service.text}</span>
                </div>
              );
            })}
          </div>
          
          {/* Closing Text */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 text-center border border-amber-100">
            <CheckCircle2 className="h-10 w-10 text-amber-600 mx-auto mb-4" />
            <p className="text-lg sm:text-xl text-carbon-black font-medium">
              Built for companies that want technology aligned with long-term growth.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Ready to optimize your systems?
          </h2>
          <p className="text-slate-gray mb-8 max-w-xl mx-auto">
            Let's discuss how automation and strategic consulting can accelerate your growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/consultation">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-slate-gray text-slate-gray hover:border-amber-500 hover:text-amber-500 transition-all">
                Book Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default ServicesSystemsPage;
